"use client";

import { useState, useEffect, useRef, useCallback, type ComponentType } from "react";
import { Tab } from "@headlessui/react";
import AppointmentsIcon from "@/icons/Appointments.svg";
import BillingIcon from "@/icons/billing.svg";
import FormulasIcon from "@/icons/formulas.svg";
import PrescriptionIcon from "@/icons/prescription.svg";
import ReportsIcon from "@/icons/reports.svg";
import LearnMoreIcon from "@/icons/learnMore.svg";

import { getTranslation } from "@/i18n/get-translation";

interface IWhyIsLucy {
  locale: string;
}

const animationImports = [
  () => import("./animations/agenda.json"),
  () => import("./animations/billing.json"),
  () => import("./animations/prescription.json"),
  () => import("./animations/reports.json"),
  () => import("./animations/formulas.json"),
];

export default function WhyIsLucy({ locale }: IWhyIsLucy) {
  const t = getTranslation(locale, "home.whyLucy");
  const [activeTab, setActiveTab] = useState(0);
  const [animations, setAnimations] = useState<Record<number, any>>({});
  const tabListRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const [PlayerComp, setPlayerComp] = useState<ComponentType<any> | null>(null);

  // Only load Lottie Player when section is visible
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible && !PlayerComp) {
      import("@lottiefiles/react-lottie-player").then(mod => {
        setPlayerComp(() => mod.Player);
      });
    }
  }, [isVisible]);

  const updateIndicator = useCallback((index: number) => {
    const tabList = tabListRef.current;
    if (!tabList) return;
    const tabs = tabList.querySelectorAll<HTMLElement>('[role="tab"]');
    const tab = tabs[index];
    if (!tab) return;
    setIndicator({
      left: tab.offsetLeft,
      width: tab.offsetWidth,
    });
  }, []);

  useEffect(() => {
    // Load the active tab's animation on demand
    if (isVisible && !animations[activeTab]) {
      animationImports[activeTab]().then((mod) => {
        setAnimations((prev) => ({ ...prev, [activeTab]: mod.default }));
      });
    }
    updateIndicator(activeTab);
  }, [activeTab, updateIndicator, isVisible]);

  useEffect(() => {
    updateIndicator(0);
  }, [updateIndicator]);

  const handleTabChange = (index: number) => {
    setActiveTab(index);
  };

  return (
    <section
      ref={sectionRef}
      id="why_is_lucy"
      className="relative text-center px-20 pt-14 pb-14 max-md:px-0"
    >
      <div className="text-2xl text-secondary-800 mb-5 max-md:text-lg font-normal">
        {t("whyLucy")}
      </div>
      <h2 className="text-[3rem] leading-[1.15] font-medium max-w-[50rem] mx-auto text-primary-800 max-md:text-3xl">
        {t("weAreYourBestChoice")}
      </h2>

      <Tab.Group onChange={handleTabChange}>
        <div ref={tabListRef} className="relative xl:w-[72rem] lg:w-[65rem] mx-auto mt-11">
        <Tab.List className="scrollbar-list border-b border-b-primary-400 text-xl font-medium text-primary-800 flex gap-16 justify-center max-md:overflow-hidden max-md:w-full max-md:overflow-x-auto max-md:px-7 max-md:justify-start">
          <Tab className="focus:outline-none pb-4">
            {({ selected }) => (
              /* Use the `selected` state to conditionally style the selected tab. */
              <div
                className={`
                  flex items-center font-medium
                  ${selected ? "text-primary-700" : "text-primary-800"}
                  `}
              >
                <AppointmentsIcon className="mr-1 max-w-[1.75rem] min-w-[1.75rem] w-full" />
                <span className="whitespace-nowrap">{t("appointments")}</span>
              </div>
            )}
          </Tab>
          <Tab className="focus:outline-none pb-4">
            {({ selected }) => (
              /* Use the `selected` state to conditionally style the selected tab. */
              <div
                className={`
                  flex items-center
                  ${selected ? "text-primary-700" : "text-primary-800"}
                  `}
              >
                <BillingIcon className="mr-1 w-7" />
                {t("billing")}
              </div>
            )}
          </Tab>
          <Tab className="focus:outline-none pb-4">
            {({ selected }) => (
              /* Use the `selected` state to conditionally style the selected tab. */
              <div
                className={`
                  flex items-center
                  ${selected ? "text-primary-700" : "text-primary-800"}
                  `}
              >
                <PrescriptionIcon className="mr-1 w-7" />
                {t("prescription")}
              </div>
            )}
          </Tab>
          <Tab className="focus:outline-none pb-4">
            {({ selected }) => (
              /* Use the `selected` state to conditionally style the selected tab. */
              <div
                className={`
                  flex items-center
                  ${selected ? "text-primary-700" : "text-primary-800"}
                  `}
              >
                <ReportsIcon className="mr-1 w-7" />

                {t("reports")}
              </div>
            )}
          </Tab>
          <Tab className="focus:outline-none pb-4">
            {({ selected }) => (
              /* Use the `selected` state to conditionally style the selected tab. */
              <div
                className={`
                  flex items-center
                  ${selected ? "text-primary-700" : "text-primary-800"}
                  `}
              >
                <FormulasIcon className="mr-1 w-7" />
                {t("formulas")}
              </div>
            )}
          </Tab>
        </Tab.List>
        <div
          className="tab-indicator absolute bottom-0 h-[2px] bg-primary-700 rounded-full"
          style={{ left: indicator.left, width: indicator.width }}
        />
        </div>
        <div className="pt-11">
          <div className="tab-panels">
            <div
              className={`tab-panel flex text-left items-center max-md:flex-wrap max-md:px-7 ${
                activeTab === 0 ? "!flex tab-panel-animate" : "hidden"
              }`}
            >
              <div className="basis-2/4 max-md:basis-auto max-md:-mx-5 overflow-hidden">
                {PlayerComp && <PlayerComp
                  style={{ aspectRatio: "16/10.4" }}
                  autoplay
                  loop
                  src={animations[0] || ""}
                  className="ml-auto !-mt-20 max-md:mr-0 max-md:!-mt-10 max-md:!mb-10"
                />}
              </div>
              <div className="basis-2/4 max-md:basis-full pl-[7.125rem] max-md:pl-0">
                <div className="max-w-[28.25rem] max-md:max-w-full">
                  <h3 className="text-[2.125rem] max-md:text-xl leading-[1.15] font-bold mx-auto text-primary-800">
                    {t("hassleFreeAppointments")}
                  </h3>
                  <p className="mt-6 text-primary-600 text-xl font-light">
                    {t("appointmentsDescription")}
                  </p>
                  <div className="flex mt-6">
                    <a
                      href={`/${locale}/medical-appointment-software`}
                      className="arrowBtn whitespace-nowrap text-primary-700 font-medium rounded-full py-2 leading-4 flex items-center"
                      aria-label="Learn more about Appointment Management"
                    >
                      {t("learnMoreAppointments")}
                      <div className="relative ml-2">
                        <div className="bg-primary-400 rounded-full w-10 h-10 opacity-[0.24]"></div>
                        <LearnMoreIcon className="w-[0.625rem] font-bold absolute left-2/4 top-2/4 transform-[translate(-50%,-50%)]" />
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div
              className={`tab-panel hidden text-left items-center max-md:flex-wrap max-md:px-7 ${
                activeTab === 1 ? "!flex tab-panel-animate" : "hidden"
              }`}
            >
              <div className="basis-2/4 max-md:basis-auto max-md:-mx-5 overflow-hidden">
                {PlayerComp && <PlayerComp
                  style={{ aspectRatio: "16/10.4" }}
                  autoplay
                  loop
                  src={animations[1] || ""}
                  className="ml-auto !-mt-20 max-md:mr-0 max-md:!-mt-10 max-md:!mb-10"
                />}
              </div>
              <div className="basis-2/4 max-md:basis-full pl-[7.125rem] max-md:pl-0">
                <div className="max-w-[28.25rem] max-md:max-w-full">
                  <h3 className="text-[2.125rem] max-md:text-xl leading-[1.15] font-bold mx-auto text-primary-800">
                    {t("hassleFreeBilling")}
                  </h3>
                  <p className="mt-6 text-primary-600 text-xl font-light">
                    {t("billingDescription")}
                  </p>
                  <div className="flex mt-6">
                    <a
                      href={`/${locale}/medical-billing-software`}
                      className="arrowBtn whitespace-nowrap text-primary-700 font-medium rounded-full py-2 leading-4 flex items-center"
                      aria-label="Learn more about Medical Billing"
                    >
                      {t("learnMoreBilling")}
                      <div className="relative ml-2">
                        <div className="bg-primary-400 rounded-full w-10 h-10 opacity-[0.24]"></div>
                        <LearnMoreIcon className="w-[0.625rem] font-bold absolute left-2/4 top-2/4 transform-[translate(-50%,-50%)]" />
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div
              className={`tab-panel hidden text-left items-center max-md:flex-wrap max-md:px-7 ${
                activeTab === 2 ? "!flex tab-panel-animate" : "hidden"
              }`}
            >
              <div className="basis-2/4 max-md:basis-auto max-md:-mx-5 overflow-hidden">
                {PlayerComp && <PlayerComp
                  style={{ aspectRatio: "16/10.4" }}
                  autoplay
                  loop
                  src={animations[2] || ""}
                  className="ml-auto !-mt-20 max-md:mr-0 max-md:!-mt-10 max-md:!mb-10"
                />}
              </div>
              <div className="basis-2/4 max-md:basis-full pl-[7.125rem] max-md:pl-0">
                <div className="max-w-[28.25rem] max-md:max-w-full">
                  <h3 className="text-[2.125rem] max-md:text-xl leading-[1.15] font-bold mx-auto text-primary-800">
                    {t("hassleFreePrescription")}
                  </h3>
                  <p className="mt-6 text-primary-600 text-xl font-light">
                    {t("prescriptionDescription")}
                  </p>
                  <div className="flex mt-6">
                    <a
                      href={`/${locale}/medical-prescription-software`}
                      className="arrowBtn whitespace-nowrap text-primary-700 font-medium rounded-full py-2 leading-4 flex items-center"
                      aria-label="Learn more about Prescriptions"
                    >
                      {t("learnMorePrescription")}
                      <div className="relative ml-2">
                        <div className="bg-primary-400 rounded-full w-10 h-10 opacity-[0.24]"></div>
                        <LearnMoreIcon className="w-[0.625rem] font-bold absolute left-2/4 top-2/4 transform-[translate(-50%,-50%)]" />
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div
              className={`tab-panel hidden text-left items-center max-md:flex-wrap max-md:px-7 ${
                activeTab === 3 ? "!flex tab-panel-animate" : "hidden"
              }`}
            >
              <div className="basis-2/4 max-md:basis-auto max-md:-mx-5 overflow-hidden">
                {PlayerComp && <PlayerComp
                  style={{ aspectRatio: "16/10.4" }}
                  autoplay
                  loop
                  src={animations[3] || ""}
                  className="ml-auto !-mt-20 max-md:mr-0 max-md:!-mt-10 max-md:!mb-10"
                />}
              </div>
              <div className="basis-2/4 max-md:basis-full pl-[7.125rem] max-md:pl-0">
                <div className="max-w-[28.25rem] max-md:max-w-full">
                  <h3 className="text-[2.125rem] max-md:text-xl leading-[1.15] font-bold mx-auto text-primary-800">
                    {t("hassleFreeReports")}
                  </h3>
                  <p className="mt-6 text-primary-600 text-xl font-light">
                    {t("reportsDescription")}
                  </p>
                  <div className="flex mt-6">
                    <a
                      href={`/${locale}/medical-report-record-software`}
                      className="arrowBtn whitespace-nowrap text-primary-700 font-medium rounded-full py-2 leading-4 flex items-center"
                      aria-label="Learn more about Medical Reports"
                    >
                      {t("learnMoreReports")}
                      <div className="relative ml-2">
                        <div className="bg-primary-400 rounded-full w-10 h-10 opacity-[0.24]"></div>
                        <LearnMoreIcon className="w-[0.625rem] font-bold absolute left-2/4 top-2/4 transform-[translate(-50%,-50%)]" />
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div
              className={`tab-panel hidden text-left items-center max-md:flex-wrap max-md:px-7 ${
                activeTab === 4 ? "!flex tab-panel-animate" : "hidden"
              }`}
            >
              <div className="basis-2/4 max-md:basis-auto max-md:-mx-5 overflow-hidden">
                {PlayerComp && <PlayerComp
                  style={{ aspectRatio: "16/10.4" }}
                  autoplay
                  loop
                  src={animations[4] || ""}
                  className="ml-auto !-mt-20 max-md:mr-0 max-md:!-mt-10 max-md:!mb-10"
                />}
              </div>
              <div className="basis-2/4 max-md:basis-full pl-[7.125rem] max-md:pl-0">
                <div className="max-w-[28.25rem] max-md:max-w-full">
                  <h3 className="text-[2.125rem] max-md:text-xl leading-[1.15] font-bold mx-auto text-primary-800">
                    {t("hassleFreeFormulas")}
                  </h3>
                  <p className="mt-6 text-primary-600 text-xl font-light">
                    {t("formulasDescription")}
                  </p>
                  <div className="flex mt-6">
                    <a
                      href={`/${locale}/health-assistant-software`}
                      className="arrowBtn whitespace-nowrap text-primary-700 font-medium rounded-full py-2 leading-4 flex items-center"
                      aria-label="Learn more about Health Assistant"
                    >
                      {t("learnMoreFormulas")}
                      <div className="relative ml-2">
                        <div className="bg-primary-400 rounded-full w-10 h-10 opacity-[0.24]"></div>
                        <LearnMoreIcon className="w-[0.625rem] font-bold absolute left-2/4 top-2/4 transform-[translate(-50%,-50%)]" />
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Tab.Group>
    </section>
  );
}
